<template>
  <b-form @submit.stop.prevent="submit" :aria-busy="state.busy">
    <b-alert
      v-if="state.error || state.errorKey"
      show
      variant="danger"
      role="alert"
    >
      {{ state.error || $t("AUTH.RECOVERY." + state.errorKey) }}
    </b-alert>

    <template v-if="state.step === 'complete'">
      <p class="recovery-message" role="status">{{ state.message }}</p>
      <b-button v-if="state.password" type="button" @click="copyPassword">
        {{ $t("COMMON.COPY_CLIPBOARD") }}
      </b-button>
    </template>

    <template v-else-if="state.step === 'otp'">
      <p>
        {{
          $t(
            "AUTH.RECOVERY." +
              (stage === "send_link" ? "VERIFY_SEND" : "VERIFY_REDEEM")
          )
        }}
      </p>
      <p>{{ $t("AUTH.RECOVERY.NEXT_CODE") }}</p>
      <b-form-group :label="$t('AUTH.LOGIN.TFA')" :label-for="inputId + '-otp'">
        <b-form-input
          :id="inputId + '-otp'"
          ref="otp"
          v-model="state.otp"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          pattern="[0-9]{6}"
          minlength="6"
          maxlength="6"
          required
          :disabled="state.busy"
        />
      </b-form-group>
      <p>
        {{
          $t("AUTH.RECOVERY.EXPIRES_IN", { seconds: state.remainingSeconds })
        }}
      </p>
    </template>

    <template v-else-if="stage === 'send_link'">
      <p>{{ $t("AUTH.FORGOT.DESC") }}</p>
      <b-form-group
        :label="$t('AUTH.INPUT.USERNAME')"
        :label-for="inputId + '-username'"
      >
        <b-form-input
          :id="inputId + '-username'"
          v-model="username"
          autocomplete="username"
          required
          :disabled="state.busy"
        />
      </b-form-group>
      <b-form-group
        :label="$t('AUTH.INPUT.EMAIL')"
        :label-for="inputId + '-email'"
      >
        <b-form-input
          :id="inputId + '-email'"
          v-model="email"
          type="email"
          autocomplete="email"
          required
          :disabled="state.busy"
        />
      </b-form-group>
    </template>

    <p v-else>{{ $t("AUTH.RESET_PASSWORD.DESC") }}</p>

    <div class="d-flex justify-content-end mt-4">
      <b-button type="button" variant="outline-secondary" @click="cancel">
        {{
          $t(
            stage === "redeem"
              ? "AUTH.RESET_PASSWORD.GO_LOGIN"
              : "COMMON.CANCEL"
          )
        }}
      </b-button>
      <b-button
        v-if="state.step !== 'complete'"
        type="submit"
        variant="primary"
        class="ml-3"
        :disabled="state.busy"
      >
        <b-spinner v-if="state.busy" small aria-hidden="true" />
        {{ $t(submitLabel) }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import { postRequest } from "@/api/request";
import { createPasswordResetFlow } from "@/common/password-reset-flow";
import copy from "clipboard-copy";

export default {
  name: "PasswordRecovery",
  props: {
    stage: { type: String, required: true },
    reference: { type: String, default: "" },
  },
  data() {
    const flow = createPasswordResetFlow(postRequest, {
      stage: this.stage,
      reference: this.reference,
    });
    return { flow, state: flow.state, username: "", email: "", timer: null };
  },
  computed: {
    inputId() {
      return "password-recovery-" + this.stage;
    },
    submitLabel() {
      if (this.state.step === "otp") return "AUTH.RECOVERY.VERIFY";
      return this.stage === "send_link"
        ? "AUTH.FORGOT.SEND"
        : "AUTH.RESET_PASSWORD.GENERATE_NEW_PASSWORD";
    },
  },
  watch: {
    "state.step"(step) {
      if (step === "otp") this.focusOtp();
    },
  },
  mounted() {
    this.timer = setInterval(() => this.flow.tick(), 1000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.flow.cancel();
  },
  methods: {
    async submit() {
      await this.flow.submit({ username: this.username, email: this.email });
      if (this.state.step === "complete" && this.stage === "send_link") {
        this.$emit("completed", this.state.message);
      } else if (this.state.step === "otp") {
        this.focusOtp();
      }
    },
    focusOtp() {
      this.$nextTick(() => {
        if (this.$refs.otp) this.$refs.otp.focus();
      });
    },
    cancel() {
      this.flow.cancel();
      this.username = "";
      this.email = "";
      this.$emit("cancel");
    },
    copyPassword() {
      copy(this.state.password).catch(() => {
        this.state.errorKey = "COPY_FAILED";
      });
    },
  },
};
</script>

<style scoped>
.recovery-message {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
